import React, { useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import SafeImage from "./SafeImage";
import { useLanguage } from "../../contexts/LanguageContext";
import { kylendarPurchaseUrls } from "../../utils/kylendarPurchase";

const options = [
  {
    id: 'Mini 4.2"',
    label: 'Mini 4.2"',
    key: "mini",
    hint: "Compact spaces",
    image: "/images/products/kylendar-mini.webp",
    purchaseUrls: kylendarPurchaseUrls.mini,
  },
  {
    id: 'Standard V1 7.5"',
    label: 'Standard V1 7.5"',
    key: "standardV1",
    hint: "Daily planning",
    image: "/images/products/kylendar-standard-v1-black-clean.png",
    purchaseUrls: kylendarPurchaseUrls.standardV1,
  },
  {
    id: 'Standard V2 7.5"',
    label: 'Standard V2 7.5"',
    key: "standardV2",
    hint: "Advanced layouts",
    image: "/images/products/kylendar-standard.webp",
    purchaseUrls: kylendarPurchaseUrls.standardV2,
  },
];

const markets = [
  "us",
  "jp",
  "uk",
];

export function detectPurchaseMarket(language, timeZone) {
  const normalizedLanguage = (language || "").toLowerCase();
  const normalizedTimeZone = (timeZone || "").toLowerCase();

  if (normalizedLanguage.startsWith("ja") || normalizedTimeZone === "asia/tokyo") return "jp";
  if (normalizedLanguage === "en-gb" || normalizedTimeZone === "europe/london") return "uk";
  return "us";
}

function getBrowserPurchaseMarket() {
  try {
    return detectPurchaseMarket(navigator.language, Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    return "us";
  }
}

export default function ConversionSection() {
  const { t } = useLanguage();
  const copy = t("kylendar.conversion");
  const [selected, setSelected] = useState(options[2].id);
  const [market, setMarket] = useState(getBrowserPurchaseMarket);
  const active = options.find((option) => option.id === selected) || options[2];
  const marketLabel = copy.markets[market] || copy.markets.us;
  const purchaseUrl = active.purchaseUrls[market];
  const unavailableLabel = copy.unavailable;

  return (
    <section id="conversion" className="relative overflow-hidden bg-brand-surface py-16 sm:py-20">
      <span className="home-orb -left-8 top-0 h-64 w-64 bg-brand-300/40" aria-hidden="true" />
      <span className="home-orb bottom-0 right-0 h-72 w-72 bg-cyan-200/30" aria-hidden="true" />
      <div className="relative mx-auto grid max-w-6xl items-center gap-8 rounded-3xl border border-brand-200/80 bg-gradient-to-br from-white via-white to-brand-50 p-6 shadow-[0_28px_60px_-36px_rgba(14,181,126,.45)] sm:p-8 lg:grid-cols-[0.95fr_1.05fr] lg:p-10">
        <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-brand-50 via-slate-50 to-emerald-50 p-4">
          <SafeImage
            src={active.image}
            alt={t("accessibility.product").replace("{model}", active.label)}
            loading="lazy"
            className="mx-auto h-56 w-full object-contain transition-transform duration-500 sm:h-64"
          />
        </div>

        <div>
          <p className="home-eyebrow">{copy.eyebrow}</p>
          <h2 className="mt-2 font-display text-3xl font-extrabold tracking-[-0.05em] text-slate-950 sm:text-4xl">
            {t("interface.buyTitle")}
          </h2>
          <p className="mt-3 max-w-md text-sm leading-6 text-slate-600">
            {copy.body}
          </p>

          <fieldset className="mt-6">
            <legend className="text-xs font-bold text-slate-700">{copy.model}</legend>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" role="radiogroup" aria-label={copy.model}>
              {options.map((option) => {
                const isActive = selected === option.id;
                const inputId = `model-${option.id.replace(/[^a-zA-Z0-9]/g, "-")}`;
                return (
                  <label
                    key={option.id}
                    htmlFor={inputId}
                    className={`cursor-pointer rounded-2xl border p-4 transition ${
                      isActive
                        ? "border-brand bg-brand-50 shadow-[0_12px_28px_-20px_rgba(14,181,126,.55)]"
                        : "border-slate-200 bg-white hover:border-brand-300"
                    }`}
                  >
                    <input
                      id={inputId}
                      type="radio"
                      name="model-choice"
                      value={option.id}
                      checked={isActive}
                      onChange={() => setSelected(option.id)}
                      aria-label={option.label}
                      className="sr-only"
                    />
                    <span className="block text-sm font-bold text-slate-950">{option.label}</span>
                    <span className="mt-1 block text-xs text-slate-500">{copy.hints[option.key]}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          <fieldset className="mt-5">
            <legend className="text-xs font-bold text-slate-700">{copy.region}</legend>
            <div className="mt-3 flex flex-wrap gap-2" role="radiogroup" aria-label={copy.region}>
              {markets.map((value) => {
                const isActive = market === value;
                const label = copy.markets[value];
                const isAvailable = Boolean(active.purchaseUrls[value]);
                return (
                  <label
                    key={value}
                    aria-disabled={!isAvailable}
                    title={!isAvailable ? unavailableLabel : undefined}
                    className={`rounded-full border px-4 py-2 text-xs font-bold transition ${
                      isActive && isAvailable
                        ? "border-brand bg-brand-50 text-brand-700"
                        : isAvailable
                          ? "cursor-pointer border-slate-200 bg-white text-slate-600 hover:border-brand-300"
                          : "cursor-not-allowed border-slate-100 bg-slate-50 text-slate-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="purchase-region"
                      value={value}
                      checked={isActive}
                      disabled={!isAvailable}
                      onChange={() => setMarket(value)}
                      aria-label={label}
                      className="sr-only"
                    />
                    {label}
                  </label>
                );
              })}
            </div>
          </fieldset>

          {purchaseUrl ? (
            <a
              href={purchaseUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-6 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-brand-700 to-brand-500 px-6 py-3.5 text-sm font-bold text-white shadow-[0_14px_28px_-14px_rgba(8,122,88,.7)] transition hover:-translate-y-0.5 hover:from-brand-800 hover:to-brand-600 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
            >
              {t(`interface.purchase.${market}`)} <ArrowRight className="h-4 w-4" />
            </a>
          ) : (
            <button type="button" disabled className="mt-6 inline-flex cursor-not-allowed items-center gap-2 rounded-xl bg-slate-200 px-6 py-3.5 text-sm font-bold text-slate-400">
              {unavailableLabel}
            </button>
          )}
          <p aria-live="polite" className="mt-3 flex items-center gap-1.5 text-xs text-slate-500">
            <CheckCircle2 className="h-3.5 w-3.5 text-brand" /> {copy.selected} {selected}{!purchaseUrl ? ` · ${marketLabel} · ${unavailableLabel}` : ""}
          </p>
        </div>
      </div>
    </section>
  );
}
